import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import getUserGeo from '@sdk/utils/api/getUserGeo';
import { SaveClientFlags } from '@sdk/authentication/types';

import { shouldDisplaySatisfactionSurvey } from '@web/components/survey/utils';
import { SurveyType } from '@web/components/survey/types';
import getUserRoleName from '@web/common/utils/getUserRoleName';

// spreadsheet url: https://docs.google.com/spreadsheets/d/1OziqQSbySlOAhn8c0YuM4S7spbpbvLioPutZyYV77A8
const FORM_URL =
  'https://script.google.com/macros/s/AKfycbzSMZ81UErfncq1NBlH60N_VKUlFVSCfXwhUxKhHZ9SH2WvaTDTW5VbOx72Y8TYmRTNOg/exec';
const POSITIVE_RATING = 5;
const BAD_RATING = 3;
const SURVEY_DATE_FLAG_KEY = 'npsRateDate'; // name is confusing as it was introduced when there was only one survey

type FormData = {
  user_id: string;
  user_name: string;
  email: string;
  role: string;
  org_id: string;
  org_name: string;
  org_creation: string;
  subscription: string;
  industry: string;
  user_count: string;
  country_name: string;
  score: string;
  comment_1: string;
  location_count: string;
};

type PsatFeedback = {
  score: number | null;
  comment1: string;
};

export function isScorePositive(score: number): boolean {
  return score >= POSITIVE_RATING;
}

export function isBadScore(score: number): boolean {
  return score <= BAD_RATING;
}

type AdditionalFeedbackData = {
  user: AuthUserType;
  subscriptionName: string;
  usersCount: number;
  locationsCount: number;
  saveClientFlags: SaveClientFlags;
};

export async function sendPsatFeedback(
  feedback: PsatFeedback,
  additionalData: AdditionalFeedbackData
): Promise<void> {
  const isValid = validatePsatFeedback(feedback);

  if (isValid && !__DEV__) {
    const geo = await getUserGeo();
    const data = getFormData(feedback, additionalData, geo.country);

    fetch(`${FORM_URL}?${new URLSearchParams(data)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  updatePsatRateDate(additionalData.saveClientFlags);
}

function validatePsatFeedback(feedback: PsatFeedback) {
  return typeof feedback.score === 'number';
}

function getFormData(
  feedback: PsatFeedback,
  additionalData: AdditionalFeedbackData,
  country: string
): FormData {
  const { user, subscriptionName, usersCount, locationsCount } = additionalData;
  const dismissedLabel = '**dismissed**';

  return {
    user_id: String(user.id),
    org_id: String(user.org.id),
    user_name: user.fullname,
    email: user.email,
    org_name: user.org.name,
    industry: user.org.businessCategory,
    subscription: subscriptionName,
    country_name: country,
    org_creation: String(user.org.createdAt).substr(0, 10),
    user_count: String(usersCount),
    role: getUserRoleName(user.role),
    score: String(feedback.score),
    comment_1: feedback.comment1 || dismissedLabel,
    location_count: String(locationsCount),
  };
}

export function shouldDisplayPsatScore(user: AuthUserType): boolean {
  return shouldDisplaySatisfactionSurvey(user, SurveyType.psat);
}

export function updatePsatRateDate(saveClientFlags: SaveClientFlags): void {
  saveClientFlags({ flags: { [SURVEY_DATE_FLAG_KEY]: new Date().toISOString() } });
}
